import { useEffect, useState } from "react";

const QUERY = "(prefers-reduced-motion: reduce)";

/**
 * Whether the user asked the OS to minimise animation.
 *
 * Anything that moves on its own — a counting balance, an auto-playing transition — must consult this and
 * jump straight to the final state instead. Same `matchMedia` + listener shape as `ThemeProvider` and
 * `use-mobile`; SSR-safe (no `window` during the first evaluation on a non-browser runtime).
 */
export function usePrefersReducedMotion(): boolean {
  const [prefersReduced, setPrefersReduced] = useState(
    () => typeof window !== "undefined" && window.matchMedia(QUERY).matches,
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const media = window.matchMedia(QUERY);
    const onChange = (event: MediaQueryListEvent) => setPrefersReduced(event.matches);
    // No sync setState here: the initializer already read `media.matches`, and this only subscribes for
    // later changes. A re-read would just cascade a render with the same value.
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  return prefersReduced;
}
